/**
 * Region Detection for Privacy Consent (P20250925-149)
 *
 * SPA-friendly heuristic to determine if user is in EEA/UK.
 * Uses timezone + language as proxy (privacy-friendly, no IP tracking).
 * Fail-closed: defaults to US/ROW (no banner) if detection fails.
 */

const EU_COUNTRIES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
]);

const EEA_TIMEZONES = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Stockholm',
  'Europe/Copenhagen',
  'Europe/Oslo',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Sofia',
  'Europe/Athens',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Zagreb',
  'Europe/Luxembourg',
  'Europe/Vilnius',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Ljubljana',
  'Europe/Bratislava',
  'Europe/Nicosia',
  'Atlantic/Reykjavik',
];

const TZ_EU_PREFIXES = ['Europe/', 'EET', 'WET', 'CET'];

// Cache the region check result to prevent flashing
let cachedRegionCheck: boolean | null = null;

/**
 * Check if user appears to be in EEA/UK region
 * Uses timezone + language heuristics (privacy-friendly)
 */
export function isEUClientHeuristic(): boolean {
  if (typeof window === 'undefined') return false;

  // Return cached result if available
  if (cachedRegionCheck !== null) {
    return cachedRegionCheck;
  }

  // Check sessionStorage first (persists across page loads)
  try {
    const stored = sessionStorage.getItem('ns_region_eea');
    if (stored !== null) {
      const isEEA = stored === '1';
      cachedRegionCheck = isEEA;
      return isEEA;
    }
  } catch {
    // Ignore storage errors
  }

  let isEU = false;

  try {
    // Check timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (EEA_TIMEZONES.includes(timezone)) {
      isEU = true;
    } else if (TZ_EU_PREFIXES.some((prefix) => timezone.startsWith(prefix))) {
      isEU = true;
    }

    // Check language as secondary signal
    if (!isEU) {
      const lang = (navigator.language || '').toUpperCase();
      const langCountry = lang.split('-')[1];
      if (langCountry && EU_COUNTRIES.has(langCountry)) {
        isEU = true;
      }
    }
  } catch {
    // If all detection fails, default to false (US/ROW, no banner)
    isEU = false;
  }

  // Cache the result
  cachedRegionCheck = isEU;

  // Store in sessionStorage
  try {
    sessionStorage.setItem('ns_region_eea', isEU ? '1' : '0');
  } catch {
    // Ignore storage errors
  }

  // Debug log in development
  if (import.meta.env.DEV) {
    console.debug('[consent] euLike=', isEU);
  }

  return isEU;
}

/**
 * Legacy alias for backward compatibility
 */
export function isEEALikeRegion(): boolean {
  return isEUClientHeuristic();
}

/**
 * Check if consent banner should be shown
 */
export function shouldShowConsentBanner(): boolean {
  return isEUClientHeuristic();
}
